import React, { useState, useMemo } from "react";
import classNames from "classnames";

import { Popover, PopoverPortal } from "@/components/molecules/Popover";
import { Button, Input } from "@/components/atoms";
import { ChevronDownIcon } from "@/components/icons";

export interface ISelectItem {
    id: string;
    text: string;
    data?: any;
}

export interface IPopoverSelectProps<T extends ISelectItem> {
    items: T[];
    selectedItem?: T;
    onSelectionChange: (selectedItem: T) => void;
    triggerLabel?: string;
    searchPlaceholder?: string;
    width?: string;
    maxHeight?: string;
    renderCustomLabel?: (item: T) => React.ReactNode;
    triggerClassName?: string;
    usePortal?: boolean;
    zIndex?: `z-${number}` | `z-[${number}]`;
    disableSearch?: boolean;
    showPopoverContent?: boolean;
    prefixIcon?: React.ReactNode;
}

export const PopoverSelect = <T extends ISelectItem>({
    items,
    selectedItem,
    onSelectionChange,
    triggerLabel = "Select",
    searchPlaceholder = "Search",
    width = "w-64",
    maxHeight = "max-h-80",
    renderCustomLabel,
    triggerClassName,
    usePortal = true,
    zIndex = "z-50",
    disableSearch = false,
    showPopoverContent = true,
    prefixIcon
}: IPopoverSelectProps<T>) => {
    const [search, setSearch] = useState<string>("");
    const [isOpen, setIsOpen] = useState<boolean>(false);

    const filteredItems = useMemo(() => {
        if (!search.trim() || disableSearch) {
            return items.sort((a, b) => a.text.toLowerCase().localeCompare(b.text.toLowerCase()));
        }

        const searchLower = search.toLowerCase();
        return items
            .filter(item => item.text.toLowerCase().includes(searchLower))
            .sort((a, b) => a.text.toLowerCase().localeCompare(b.text.toLowerCase()));
    }, [items, search, disableSearch]);

    const handleItemClick = (item: T) => {
        onSelectionChange(item);
        setSearch(""); // Clear search when selection is made
    };

    const triggerButton = (
        <Button 
            className={classNames(triggerClassName, {
                '!border-[#2D3A3F]': isOpen
            })} 
            variant={selectedItem ? "filterApplied" : "filter"} 
            type="button"
            size="sm"
        >
            <span style={{ fontFamily: 'Roboto, sans-serif' }} className="flex items-center gap-1">
                {prefixIcon && <span className="text-current flex items-center" style={{ height: '1em', marginTop: '-1px' }}>{prefixIcon}</span>}
                {selectedItem ? (
                    <>
                        <span style={{ fontWeight: 'bold' }}>{triggerLabel}</span>
                        <span style={{ fontWeight: 'normal' }}> {selectedItem.text}</span>
                    </>
                ) : (
                    <span style={{ fontWeight: 'bold' }}>{triggerLabel}</span>
                )}
            </span>{" "}
            <ChevronDownIcon className="h-4 w-4" />
        </Button>
    );

    const popoverContent = (
        <div
            className={classNames("pt-4 pb-2", width, maxHeight, "flex flex-col")}
            onDrag={(e) => e.stopPropagation()}
            onDragOver={(e) => e.stopPropagation()}
            onKeyDown={e => e.stopPropagation()}
            onKeyUp={e => e.stopPropagation()}
            onWheel={e => e.stopPropagation()}
        >
            {!disableSearch && (
                <div className={classNames("border-b px-3 border-gray-200 pb-2 mb-3 flex-shrink-0")}>
                    <Input
                        placeholder={searchPlaceholder}
                        type="search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
            )}

            <ul className={classNames("space-y-0 pl-3 py-2 flex-1 overflow-y-auto")}>
                {filteredItems.map((item, index) => {
                    const isSelected = selectedItem?.id === item.id;
                    return (
                        <li
                            key={item.id}
                            className={classNames(
                                "w-full cursor-pointer py-2 px-2 rounded-md transition-colors",
                                index === filteredItems.length - 1 ? "pb-1" : "",
                                isSelected ? "bg-[#079a95] text-white" : "hover:bg-gray-100"
                            )}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleItemClick(item);
                            }}
                        >
                            <div className="truncate min-w-0 max-w-46 font-roboto" title={item.text}>
                                {renderCustomLabel ? renderCustomLabel(item) : item.text}
                            </div>
                        </li>
                    );
                })}
            </ul>
        </div>
    );

    if (usePortal) {
        return (
            <PopoverPortal
                align="right"
                width={width}
                zIndex={zIndex}
                trigger={triggerButton}
                onOpenChange={setIsOpen}
            >
                {showPopoverContent && popoverContent}
            </PopoverPortal>
        );
    }

    return (
        <Popover
            align="right"
            width={width}
            trigger={triggerButton}
        >
            {popoverContent}
        </Popover>
    );
};
